import alertService from '@jetbrains/ring-ui/components/alert-service/alert-service'
import Button from '@jetbrains/ring-ui/components/button/button'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import type {HTMLAttributeAnchorTarget, MouseEvent} from 'react'
import {memo, useCallback, useEffect, useMemo} from 'react'
import type {NavigateFunction} from 'react-router-dom'
import {useNavigate} from 'react-router-dom'

import {useUpdateResults} from '../../../../../../contexts/update'
import useMyId from '../../../../../../hooks/useMyId'
import {getOverviewHref} from '../../../../../../routes'
import {restApi} from '../../../../../../services/rest'
import type {ActiveEntityURLProps} from '../../../../../../types'
import {noop} from '../../../../../../utils/empty'
import {subscribeOnUserEvents} from '../../../../../../utils/subscriber'
import {USER_PERMISSIONS_CHANGED} from '../../../../../../utils/subscriptionEvents'
import {getActionsPath} from '../../../../../common/ActionsDropdown/ActionsDropdown.utils'
import InjectHtml from '../../../../../common/InjectHtml/InjectHtml'
import {useSequenceLoaderReadyHandler} from '../../../../../common/SequenceLoader/SequenceLoader.hooks'
import {Styles} from '../../../../../common/Styles/Styles'

import styles from './ActionButtons.css'

type Props = {
  seqKey?: string
  className?: string
  entityHref?: string | null | undefined
  update?: () => unknown
}

type ActionResult = {
  redirectTo?: ActiveEntityURLProps
  alert?: string
}

type ButtonProps = {
  text: string
  href: string | undefined | null
  target: HTMLAttributeAnchorTarget | undefined
  onClick: ((event: MouseEvent<EventTarget> | null | undefined) => void) | undefined
}

const parser = new DOMParser()

function htmlToButtonProps(
  html: string,
  navigate: NavigateFunction,
  update: () => unknown,
): ButtonProps[] {
  const root = parser.parseFromString(html, 'text/html')
  const links = [...root.querySelectorAll('a')].filter(link => link.closest('.modalDialog') == null)
  return links.map(link => {
    const onclick = link.getAttribute('onclick')
    const handleClick = onclick != null ? (new Function('event', onclick) as any) : null // eslint-disable-line no-new-func

    return {
      href: link.getAttribute('href'),
      text: link.textContent?.trim() || link.title,
      target: link.getAttribute('target') ?? undefined,
      onClick:
        handleClick != null
          ? async e => {
              const result: ActionResult | null | undefined = await handleClick(e)
              const {redirectTo, alert} = result ?? {}
              if (redirectTo != null) {
                navigate(getOverviewHref(redirectTo))
                return
              }
              if (alert != null) {
                alertService.message(alert)
              }

              update()
            }
          : undefined,
    }
  })
}

function ActionButtons({className, entityHref, update = noop}: Props) {
  const updateFromContext = useUpdateResults()
  const navigate = useNavigate()

  const path = useMemo(() => getActionsPath(entityHref), [entityHref])
  const myId = useMyId()
  const [fetchHtml, {html, isLoading}] = restApi.endpoints.getHtml.useLazyQuery({
    selectFromResult: ({data, currentData}) => ({
      html: data,
      isLoading: data !== currentData,
    }),
  })
  const reload = useCallback(() => path != null && fetchHtml({path}), [fetchHtml, path])

  useEffect(
    () =>
      path != null && myId != null
        ? subscribeOnUserEvents(`html:${path}`, myId, [USER_PERMISSIONS_CHANGED], reload)
        : undefined,
    [myId, path, reload],
  )
  useSequenceLoaderReadyHandler(html != null)

  const htmlAvailable = html != null && html.trim() !== ''

  const htmlItems = useMemo(() => {
    if (path == null || (html != null && !htmlAvailable)) {
      return []
    }

    function handleUpdate() {
      const updateFn = update ?? updateFromContext
      updateFn()
      reload()
    }

    return html != null ? htmlToButtonProps(html, navigate, handleUpdate) : []
  }, [html, htmlAvailable, navigate, path, reload, update, updateFromContext])

  if (!htmlAvailable) {
    return null
  }

  return (
    <div className={className}>
      {html != null && htmlAvailable && <Styles html={html} />}
      {htmlItems.map(({text, target, href, onClick}) => (
        <Button
          key={text}
          className={styles.button}
          disabled={isLoading}
          {...(onClick != null ? {onClick} : {})}
          {...(href != null && href !== '#' ? {href, target} : {})}
        >
          {text}
        </Button>
      ))}
      {isLoading && <LoaderInline />}
      {path != null && (
        <div hidden>
          <InjectHtml path={path} />
        </div>
      )}
    </div>
  )
}

export default memo<Props>(ActionButtons)
